/**
 * @unscoped — SettingsPage 의 i18n — Cycle 59.
 *
 * 사용자 영역 (SettingsPage 메인 + ProfileSection + PasswordSection + EnvironmentSection +
 * ExternalProfileSection) 의 응집 cleanup. admin 영역 3 sections 는 cycle 60 분리 —
 * cycle 55-56 의 OnboardingWizard 2-cycle cleanup 패턴 재적용.
 *
 * cycle 51 calendar-i18n / cycle 54 mail-i18n / cycle 55 onboarding-i18n /
 * cycle 57 board-i18n / cycle 58 task-i18n 통합 패턴 일관.
 *
 * 정책 (cycle 46/49 학습 적용):
 *   - fallback chain: lang → en → FALLBACK_LANG (ko)
 *   - 전수 non-empty 가드는 본 모듈 test 가 강제
 *   - params 치환 ({name}) 지원
 */

import {
  getSharedLang, setSharedLang, subscribeSharedLang,
  FALLBACK_LANG, type Lang,
} from './base-i18n';

const MESSAGES: Record<string, Record<Lang, string>> = {
  // ===== SettingsPage 메인 =====
  'settings.heading': { ko: '설정', en: 'Settings', ja: '設定' },
  'settings.subtitle': {
    ko: '환경·프로필·보안을 한 곳에서 관리하세요',
    en: 'Manage your preferences, profile, and security in one place',
    ja: '環境・プロフィール・セキュリティを一括管理',
  },
  // ===== 목적별 탭 라벨 =====
  'settings.tab.general': { ko: '일반', en: 'General', ja: '一般' },
  'settings.tab.profile': { ko: '프로필', en: 'Profile', ja: 'プロフィール' },
  'settings.tab.notifications': { ko: '알림 연동', en: 'Notifications', ja: '通知連携' },
  // 통합설정 › 회사정보 (2026-08-25) — 관리 콘솔의 같은 화면을 복사해 온 탭. 관리자에게만 보인다.
  'settings.tab.company': { ko: '회사정보', en: 'Company', ja: '会社情報' },
  // 2026-08-25 — 회사 관리자 화면 8종을 한 패널로 묶으면서 탭 이름이 넓어졌다.
  //   위 'settings.tab.company' 는 패널 안의 '회사정보' 항목 이름으로 남는다(기존 키 보존).
  'settings.tab.company_admin': { ko: '회사 관리', en: 'Company admin', ja: '会社管理' },
  // ===== 통합설정 = 회사 관리자 화면 (2026-08-25) =====
  // 개인 설정 세 탭을 걷어내면서 이 화면의 이름과 설명이 바뀐다. 기존 'settings.heading' ·
  // 'settings.subtitle' 은 다른 곳(내 프로필·설정 팝업)이 쓰므로 그대로 두고 새 키를 더한다.
  'settings.company.heading': { ko: '통합설정', en: 'Company settings', ja: '統合設定' },
  'settings.company.subtitle': {
    ko: '회사 전체에 적용되는 설정입니다 — 개인 설정은 내 프로필에서 관리하세요',
    en: 'Settings that apply to the whole company — manage your own in My profile',
    ja: '会社全体に適用される設定です — 個人設定はマイプロフィールで',
  },
  'settings.personal.heading': {
    ko: '여기는 회사 관리자 화면입니다',
    en: 'This page is for company admins',
    ja: 'この画面は会社管理者用です',
  },
  'settings.personal.body': {
    ko: '테마·언어·알림·프로필·비밀번호 같은 내 설정은 내 프로필에서 바꿉니다.',
    en: 'Your own theme, language, notifications, profile, and password live in My profile.',
    ja: 'テーマ・言語・通知・プロフィール・パスワードはマイプロフィールで変更します。',
  },
  'settings.personal.cta': { ko: '내 프로필 열기', en: 'Open My profile', ja: 'マイプロフィールを開く' },
  'settings.tab.admin': { ko: '관리자', en: 'Admin', ja: '管理者' },
  'settings.tab.aiProvider': { ko: 'AI Provider', en: 'AI Provider', ja: 'AIプロバイダ' },
  'settings.tab.aiTools': { ko: 'AI 도구', en: 'AI Tools', ja: 'AIツール' },
  // 내 프로필 4탭(2026-06-07) — profile 는 위에 존재. appearance/calendar_theme/security 추가.
  'settings.tab.appearance': { ko: '화면·외관', en: 'Appearance', ja: '画面・外観' },
  'settings.tab.calendar_theme': { ko: '캘린더 테마', en: 'Calendar theme', ja: 'カレンダーテーマ' },
  'settings.tab.security': { ko: '보안', en: 'Security', ja: 'セキュリティ' },
  // 캘린더 테마 탭
  'settings.caltheme.heading': { ko: '캘린더 테마', en: 'Calendar theme', ja: 'カレンダーテーマ' },
  'settings.caltheme.subtitle': {
    ko: '캘린더에 적용할 색 테마를 선택하세요. 선택하면 즉시 반영됩니다.',
    en: 'Choose a color theme for your calendar. It applies instantly.',
    ja: 'カレンダーに適用する色テーマを選択してください。即時反映されます。',
  },
  'settings.caltheme.empty': { ko: '사용 가능한 테마가 없습니다.', en: 'No themes available.', ja: '利用可能なテーマがありません。' },
  'settings.caltheme.missing': {
    ko: '선택한 테마가 비활성화되었거나 삭제되어 기본 테마가 적용 중입니다.',
    en: 'Your selected theme was disabled or removed; the default theme is applied.',
    ja: '選択したテーマが無効化または削除されたため、既定のテーマを適用中です。',
  },

  // ===== AI Provider 탭 =====
  'settings.aiProvider.heading': {
    ko: 'AI Provider',
    en: 'AI Provider',
    ja: 'AIプロバイダ',
  },
  'settings.aiProvider.subtitle': {
    ko: '현재 시스템에 감지된 LLM 제공자입니다. 환경변수(env) 와 CLI 도구를 자동 인식합니다.',
    en: 'LLM providers currently detected on this system. Auto-detected from environment variables (env) and CLI tools.',
    ja: '現在のシステムで検出されたLLMプロバイダです。環境変数(env)とCLIツールを自動認識します。',
  },
  'settings.aiProvider.section.env': {
    ko: '환경변수 기반 (env)',
    en: 'Environment variables (env)',
    ja: '環境変数ベース (env)',
  },
  'settings.aiProvider.section.cli': {
    ko: 'CLI 도구 기반',
    en: 'CLI tools',
    ja: 'CLIツールベース',
  },
  'settings.aiProvider.activeBadge': { ko: '활성', en: 'Active', ja: 'アクティブ' },
  'settings.aiProvider.field.baseUrl': { ko: 'Base URL', en: 'Base URL', ja: 'Base URL' },
  'settings.aiProvider.field.apiKey': { ko: 'API Key', en: 'API Key', ja: 'APIキー' },
  'settings.aiProvider.field.envFile': { ko: 'env 파일', en: 'env file', ja: 'envファイル' },
  'settings.aiProvider.field.envKeys': { ko: 'env 키', en: 'env keys', ja: 'envキー' },
  'settings.aiProvider.field.bin': { ko: '실행 경로', en: 'Binary path', ja: '実行パス' },
  'settings.aiProvider.field.version': { ko: '버전', en: 'Version', ja: 'バージョン' },
  'settings.aiProvider.empty.env': {
    ko: '감지된 환경변수 기반 provider 가 없습니다. .env 에 OPENAI_API_KEY 등을 설정하세요.',
    en: 'No env-based providers detected. Set OPENAI_API_KEY etc. in your .env.',
    ja: '環境変数ベースのプロバイダが検出されませんでした。.envにOPENAI_API_KEY等を設定してください。',
  },
  'settings.aiProvider.empty.cli': {
    ko: '감지된 CLI 도구가 없습니다.',
    en: 'No CLI tools detected.',
    ja: 'CLIツールが検出されませんでした。',
  },
  'settings.aiProvider.error.load': {
    ko: '정보를 불러오지 못했습니다.',
    en: 'Failed to load.',
    ja: '情報を読み込めませんでした。',
  },
  'settings.aiProvider.loading': { ko: '불러오는 중…', en: 'Loading…', ja: '読み込み中...' },
  'settings.aiProvider.btn.activate': {
    ko: '활성화',
    en: 'Activate',
    ja: 'アクティブにする',
  },
  'settings.aiProvider.btn.deactivate': {
    ko: '비활성화',
    en: 'Deactivate',
    ja: '無効にする',
  },
  'settings.aiProvider.saving': { ko: '적용 중…', en: 'Applying…', ja: '適用中...' },
  'settings.aiProvider.btn.healthCheck': { ko: '연결 테스트', en: 'Test connection', ja: '接続テスト' },
  'settings.aiProvider.health.checking': { ko: '확인 중…', en: 'Checking…', ja: '確認中...' },
  'settings.aiProvider.health.ok': { ko: '연결 정상', en: 'Connected', ja: '接続正常' },
  'settings.aiProvider.health.fail': { ko: '연결 실패', en: 'Failed', ja: '接続失敗' },
  'settings.aiProvider.error.save': {
    ko: '선택을 저장하지 못했습니다.',
    en: 'Failed to save selection.',
    ja: '選択を保存できませんでした。',
  },
  'settings.aiProvider.scope.user': {
    ko: '내 설정',
    en: 'My selection',
    ja: '個人設定',
  },
  'settings.aiProvider.scope.global': {
    ko: '기관 기본값',
    en: 'Org default',
    ja: '機関デフォルト',
  },
  'settings.aiProvider.scope.default': {
    ko: '자동 (fallback)',
    en: 'Auto (fallback)',
    ja: '自動 (fallback)',
  },
  'settings.aiProvider.field.model': { ko: '모델', en: 'Model', ja: 'モデル' },
  'settings.aiProvider.model.useDefault': {
    ko: '기본 (env 설정 사용)',
    en: 'Default (env)',
    ja: 'デフォルト (env)',
  },
  'settings.aiProvider.model.loading': {
    ko: '모델 목록 불러오는 중…',
    en: 'Loading models…',
    ja: 'モデルを読み込み中...',
  },
  'settings.aiProvider.model.unsupported': {
    ko: 'CLI provider — 모델 선택 미지원 (CLI 기본값 사용)',
    en: 'CLI provider — model selection unsupported',
    ja: 'CLIプロバイダ — モデル選択は未対応',
  },
  'settings.aiProvider.model.empty': {
    ko: '감지된 모델이 없습니다.',
    en: 'No models detected.',
    ja: 'モデルが検出されませんでした。',
  },
  'settings.aiProvider.model.error': {
    ko: '모델 목록을 불러오지 못했습니다.',
    en: 'Failed to load models.',
    ja: 'モデルリストを読み込めませんでした。',
  },
  'settings.aiProvider.activeModel': {
    ko: '활성 모델',
    en: 'Active model',
    ja: 'アクティブモデル',
  },

  // ===== 공통 버튼 =====
  'settings.btn.save': { ko: '저장', en: 'Save', ja: '保存' },
  'settings.btn.saving': { ko: '저장 중…', en: 'Saving…', ja: '保存中...' },
  'settings.msg.saved': { ko: '저장됨', en: 'Saved', ja: '保存しました' },
  'settings.msg.saved_punct': { ko: '저장되었습니다.', en: 'Saved.', ja: '保存しました。' },

  // ===== ProfileSection (SET-005) =====
  'settings.profile.heading': { ko: '프로필', en: 'Profile', ja: 'プロフィール' },
  'settings.profile.login_required': {
    ko: '로그인이 필요합니다.',
    en: 'Login required.',
    ja: 'ログインが必要です。',
  },
  'settings.profile.label_name': { ko: '이름', en: 'Name', ja: '名前' },
  'settings.profile.label_position': { ko: '직책', en: 'Position', ja: '役職' },
  // 2026-08-27 — 직위는 본인이 못 바꾼다(발령·담당자만). 칸을 지우지 않고 «왜» 를 곁에 적는다.
  'settings.profile.position_readonly': {
    ko: '인사 담당자와 발령으로만 바뀝니다 — 고칠 것이 있으면 정정 요청으로 알려 주세요',
    en: 'Changed only by HR or a personnel action — file a correction request',
    ja: '人事担当と発令でのみ変更されます — 訂正依頼でお知らせください',
  },
  'settings.profile.placeholder_position': { ko: '예: 매니저', en: 'e.g. Manager', ja: '例: マネージャー' },
  'settings.profile.label_phone': { ko: '전화', en: 'Phone', ja: '電話' },
  'settings.profile.placeholder_phone': {
    ko: '예: 010-0000-0000',
    en: 'e.g. 010-0000-0000',
    ja: '例: 010-0000-0000',
  },
  'settings.profile.label_avatar': { ko: '아바타 URL', en: 'Avatar URL', ja: 'アバターURL' },
  // 프로필 이미지 업로더 (P2)
  'settings.profile.photo_heading': { ko: '프로필 사진', en: 'Profile photo', ja: 'プロフィール写真' },
  'settings.profile.photo_change': { ko: '사진 변경', en: 'Change photo', ja: '写真を変更' },
  'settings.profile.photo_remove': { ko: '삭제', en: 'Remove', ja: '削除' },
  'settings.profile.photo_uploading': { ko: '업로드 중…', en: 'Uploading…', ja: 'アップロード中...' },
  'settings.profile.photo_hint': {
    ko: 'PNG·JPG·WEBP, 2MB 이하 (정사각으로 자동 조정)',
    en: 'PNG/JPG/WEBP, up to 2MB (auto square crop)',
    ja: 'PNG・JPG・WEBP、2MB以下（自動で正方形に調整）',
  },
  'settings.profile.photo_err_type': {
    ko: '이미지 파일(PNG·JPG·WEBP·GIF)만 가능합니다',
    en: 'Only image files (PNG/JPG/WEBP/GIF) are allowed',
    ja: '画像ファイル（PNG・JPG・WEBP・GIF）のみ可能です',
  },
  'settings.profile.photo_err_size': {
    ko: '이미지가 너무 큽니다 (최대 10MB 원본)',
    en: 'Image is too large (max 10MB source)',
    ja: '画像が大きすぎます（元画像は最大10MB）',
  },
  'settings.profile.photo_err_upload': {
    ko: '업로드 실패: {msg}',
    en: 'Upload failed: {msg}',
    ja: 'アップロード失敗: {msg}',
  },
  // 내 프로필 페이지(/me) — 프로필 설정 전용 화면 (design-20260606 §17.4)
  'settings.myprofile.heading': { ko: '내 프로필', en: 'My Profile', ja: 'マイプロフィール' },
  'settings.myprofile.logout': { ko: '로그아웃', en: 'Sign out', ja: 'ログアウト' },
  'settings.myprofile.logout_confirm': {
    ko: '로그아웃하시겠습니까?',
    en: 'Are you sure you want to sign out?',
    ja: 'ログアウトしますか？',
  },
  // v2.14 — 로그아웃을 프로필 탭 하단 계정 액션 카드로 이전.
  'settings.myprofile.logout_title': { ko: '계정 로그아웃', en: 'Sign out', ja: 'アカウントからログアウト' },
  'settings.myprofile.logout_desc': {
    ko: '현재 기기에서 로그아웃합니다. 다시 로그인하면 이어서 사용할 수 있어요.',
    en: 'Sign out from this device. Log back in anytime to continue.',
    ja: 'この端末からログアウトします。再ログインで続けて利用できます。',
  },
  'settings.myprofile.subtitle': {
    ko: '프로필 사진과 기본 정보, 비밀번호를 관리합니다.',
    en: 'Manage your profile photo, basic info, and password.',
    ja: 'プロフィール写真・基本情報・パスワードを管理します。',
  },
  'settings.profile.card_basic': { ko: '기본 정보', en: 'Basic info', ja: '基本情報' },
  'settings.profile.label_email': { ko: '이메일', en: 'Email', ja: 'メール' },
  'settings.profile.label_role': { ko: '권한', en: 'Role', ja: '権限' },
  'settings.profile.role_staff': { ko: '일반', en: 'Staff', ja: '一般' },
  'settings.profile.role_manager': { ko: '매니저', en: 'Manager', ja: 'マネージャー' },
  'settings.profile.role_admin': { ko: '관리자', en: 'Admin', ja: '管理者' },
  'settings.profile.role_system_admin': { ko: '시스템 관리자', en: 'System admin', ja: 'システム管理者' },
  'settings.profile.err_save': {
    ko: '저장 실패: {msg}',
    en: 'Save failed: {msg}',
    ja: '保存失敗: {msg}',
  },
  'settings.profile.err_unknown': {
    ko: '알 수 없는 오류',
    en: 'Unknown error',
    ja: '不明なエラー',
  },

  // ===== PasswordSection (SET-006) =====
  'settings.password.heading': { ko: '비밀번호 변경', en: 'Change password', ja: 'パスワード変更' },
  'settings.password.label_current': { ko: '현재 비밀번호', en: 'Current password', ja: '現在のパスワード' },
  'settings.password.label_new': { ko: '새 비밀번호', en: 'New password', ja: '新しいパスワード' },
  'settings.password.label_confirm': { ko: '새 비밀번호 확인', en: 'Confirm new password', ja: '新しいパスワード (確認)' },
  'settings.password.btn_change': { ko: '변경', en: 'Change', ja: '変更' },
  'settings.password.hint': {
    ko: '8자 이상, 영문·숫자·특수문자 중 3종 이상 포함',
    en: 'At least 8 characters with 3+ of letters, numbers, symbols',
    ja: '8文字以上、英字・数字・記号のうち3種類以上',
  },
  'settings.password.btn_changing': { ko: '변경 중…', en: 'Changing…', ja: '変更中...' },
  'settings.password.msg_success': {
    ko: '비밀번호가 변경되었습니다',
    en: 'Password changed',
    ja: 'パスワードが変更されました',
  },
  // 2026-09-15 — 비밀번호를 바꾸면 모든 기기의 토큰이 폐기된다(서버 PR #1365).
  'settings.password.msg_relogin': {
    ko: '모든 기기에서 로그아웃됩니다 — 새 비밀번호로 다시 로그인해 주세요.',
    en: 'You will be signed out on all devices — please sign in again with your new password.',
    ja: 'すべてのデバイスでログアウトされます — 新しいパスワードで再度ログインしてください。',
  },
  'settings.password.btn_relogin': { ko: '다시 로그인', en: 'Sign in again', ja: '再ログイン' },
  'settings.password.err_mismatch': {
    ko: '새 비밀번호와 확인이 일치하지 않습니다',
    en: 'New password and confirmation do not match',
    ja: '新しいパスワードと確認が一致しません',
  },
  'settings.password.err_min_length': {
    ko: '최소 {n}자 이상',
    en: 'At least {n} characters',
    ja: '最低{n}文字以上',
  },
  'settings.password.err_categories': {
    ko: '영문·숫자·특수문자 중 3종 이상 포함',
    en: 'Include 3+ of: letters / digits / symbols',
    ja: '英字・数字・記号のうち3種類以上を含めてください',
  },
  'settings.password.err_current_required': {
    ko: '현재 비밀번호를 입력하세요',
    en: 'Please enter your current password',
    ja: '現在のパスワードを入力してください',
  },
  'settings.password.err_invalid': {
    ko: '현재 비밀번호가 일치하지 않거나 비밀번호 변경이 불가한 계정입니다',
    en: 'Current password is incorrect, or this account cannot change passwords',
    ja: '現在のパスワードが一致しないか、変更不可のアカウントです',
  },
  'settings.password.err_policy': {
    ko: '비밀번호 정책 위반: {violations}',
    en: 'Password policy violated: {violations}',
    ja: 'パスワードポリシー違反: {violations}',
  },
  'settings.password.err_reused': {
    ko: '새 비밀번호가 현재 비밀번호와 동일합니다',
    en: 'New password is the same as the current password',
    ja: '新しいパスワードが現在のパスワードと同じです',
  },
  'settings.password.err_change': {
    ko: '변경 실패: {message}',
    en: 'Change failed: {message}',
    ja: '変更失敗: {message}',
  },

  // ===== EnvironmentSection (SET-001~004) =====
  'settings.env.heading': { ko: '환경설정', en: 'Preferences', ja: '環境設定' },
  'settings.env.loading': { ko: '로드 중…', en: 'Loading…', ja: '読み込み中...' },
  'settings.env.err_load': {
    ko: '설정 로드 실패: {msg}',
    en: 'Failed to load settings: {msg}',
    ja: '設定読み込み失敗: {msg}',
  },
  'settings.env.err_save': {
    ko: '저장 실패: {msg}',
    en: 'Save failed: {msg}',
    ja: '保存失敗: {msg}',
  },
  'settings.env.label_theme': { ko: '테마 색상', en: 'Theme color', ja: 'テーマカラー' },
  'settings.env.theme.default': { ko: '기본', en: 'Default', ja: '既定' },
  'settings.env.theme.ocean_blue': { ko: '오션 블루', en: 'Ocean Blue', ja: 'オーシャンブルー' },
  'settings.env.theme.sunset_orange': { ko: '선셋 오렌지', en: 'Sunset Orange', ja: 'サンセットオレンジ' },
  'settings.env.theme.forest_green': { ko: '포레스트 그린', en: 'Forest Green', ja: 'フォレストグリーン' },
  'settings.env.theme.royal_purple': { ko: '로열 퍼플', en: 'Royal Purple', ja: 'ロイヤルパープル' },
  'settings.env.theme.rose_pink': { ko: '로즈 핑크', en: 'Rose Pink', ja: 'ローズピンク' },
  'settings.env.label_dark_mode': { ko: '다크 모드', en: 'Dark mode', ja: 'ダークモード' },
  'settings.env.label_language': { ko: '언어', en: 'Language', ja: '言語' },
  // cycle 45 ja 추가 시 LANGUAGES list 갱신 누락 → cycle 59 보강 (3 lang 일관).
  'settings.env.lang.ko': { ko: '한국어', en: 'Korean', ja: '韓国語' },
  'settings.env.lang.en': { ko: '영어', en: 'English', ja: '英語' },
  'settings.env.lang.ja': { ko: '일본어', en: 'Japanese', ja: '日本語' },
  'settings.env.label_notif_chat': { ko: '채팅 알림', en: 'Chat notifications', ja: 'チャット通知' },
  'settings.env.label_notif_mail': { ko: '메일 알림', en: 'Mail notifications', ja: 'メール通知' },
  'settings.env.label_notif_calendar': { ko: '캘린더 알림', en: 'Calendar notifications', ja: 'カレンダー通知' },
  'settings.env.label_notif_task': { ko: '태스크 알림', en: 'Task notifications', ja: 'タスク通知' },
  'settings.env.msg_pending': { ko: '저장 중…', en: 'Saving…', ja: '保存中...' },

  // ===== AppearanceSection (화면·외관, §17.2 P3) =====
  'settings.appearance.heading': { ko: '화면·외관', en: 'Appearance', ja: '画面・外観' },
  'settings.appearance.subtitle': {
    ko: '테마 색상과 라이트·다크·자동 모드를 선택합니다. 변경은 즉시 화면에 반영됩니다.',
    en: 'Choose theme color and light/dark/auto mode. Changes apply to the screen instantly.',
    ja: 'テーマカラーとライト・ダーク・自動モードを選択します。変更は即座に画面へ反映されます。',
  },
  'settings.appearance.label_mode': { ko: '화면 모드', en: 'Display mode', ja: '画面モード' },
  'settings.appearance.mode.light': { ko: '라이트', en: 'Light', ja: 'ライト' },
  'settings.appearance.mode.dark': { ko: '다크', en: 'Dark', ja: 'ダーク' },
  'settings.appearance.mode.auto': { ko: '자동', en: 'Auto', ja: '自動' },
  'settings.appearance.mode.auto_hint': {
    ko: '운영체제 설정에 맞춰 라이트·다크가 자동 전환됩니다.',
    en: 'Switches between light and dark following your OS setting.',
    ja: 'OS設定に合わせてライト・ダークが自動で切り替わります。',
  },

  // ===== ExternalProfileSection =====
  'settings.external.heading': {
    ko: '외부 알림 연동',
    en: 'External notification integrations',
    ja: '外部通知連携',
  },
  'settings.external.desc': {
    ko: 'Slack · 외부 이메일 · 모바일 푸시 매핑. 비워두면 해당 채널로 알림이 가지 않습니다.',
    en: 'Slack / external email / mobile push mapping. Leave blank to disable that channel.',
    ja: 'Slack・外部メール・モバイルプッシュのマッピング。空欄にすると該当チャネルには通知されません。',
  },
  'settings.external.last_updated': {
    ko: ' 마지막 갱신: {date}',
    en: ' Last updated: {date}',
    ja: ' 最終更新: {date}',
  },
  // ⚠️ test 의존성 — 'Slack User ID' / '외부 이메일' / 'FCM Device Token' 의 ko 텍스트는
  // ExternalProfileSection.test.tsx 의 screen.getByLabelText 가 사용. ko 텍스트 보존.
  'settings.external.label_slack': { ko: 'Slack User ID', en: 'Slack User ID', ja: 'Slack ユーザーID' },
  'settings.external.placeholder_slack': {
    ko: 'U01ABC2345 (Slack 워크스페이스의 본인 user ID)',
    en: 'U01ABC2345 (your Slack workspace user ID)',
    ja: 'U01ABC2345 (Slackワークスペースの自分のユーザーID)',
  },
  'settings.external.label_email': { ko: '외부 이메일', en: 'External email', ja: '外部メール' },
  'settings.external.placeholder_email': { ko: 'me@external.com', en: 'me@external.com', ja: 'me@external.com' },
  /* 판 P — 이 칸이 무엇을 하는지 아무도 몰라서 아무도 안 채웠다(운영 실측 0건). 화면이 적는다. */
  'settings.external.hint_email': {
    ko: '여기에 주소를 넣으면 알림을 메일로도 받습니다. 비워 두면 안 받습니다. 어떤 종류를 메일로 보낼지는 회사가 정합니다.',
    en: 'Enter an address here to also receive notifications by email. Leave blank to opt out. Which kinds are sent by email is set by your company.',
    ja: 'ここにアドレスを入れると通知をメールでも受け取ります。空欄なら受け取りません。どの種類をメールで送るかは会社が決めます。',
  },
  'settings.external.label_fcm': { ko: 'FCM Device Token', en: 'FCM Device Token', ja: 'FCM デバイストークン' },
  'settings.external.placeholder_fcm': {
    ko: '모바일 앱이 발급한 FCM token',
    en: 'FCM token issued by your mobile app',
    ja: 'モバイルアプリが発行したFCMトークン',
  },
  'settings.external.err_load': {
    ko: '외부 매핑 로드 실패: {msg}',
    en: 'Failed to load external mapping: {msg}',
    ja: '外部マッピング読み込み失敗: {msg}',
  },
  'settings.external.err_save': {
    ko: '저장 실패: {msg}',
    en: 'Save failed: {msg}',
    ja: '保存失敗: {msg}',
  },

  // ===== Cycle 60 — admin sections (AdminExternal + AdminFaq + AdminBulkImport) =====

  // ----- AdminExternalProfileSection -----
  'settings.admin_external.heading': {
    ko: 'ADMIN — 다른 사용자 외부 매핑',
    en: 'ADMIN — Other users\' external mapping',
    ja: 'ADMIN — 他ユーザーの外部マッピング',
  },
  'settings.admin_external.desc': {
    ko: 'ADMIN 권한 — 본인 외 다른 사용자 매핑 편집. audit 에 byAdmin=true 영구 기록.',
    en: 'ADMIN privileges — edit mappings for other users. byAdmin=true is permanently recorded in audit.',
    ja: 'ADMIN 権限 — 自分以外のユーザーマッピングを編集。audit に byAdmin=true を永続記録。',
  },
  'settings.admin_external.label_target': { ko: '대상 User ID', en: 'Target User ID', ja: '対象 User ID' },
  'settings.admin_external.placeholder_target': { ko: '숫자 user ID', en: 'numeric user ID', ja: '数値 user ID' },
  'settings.admin_external.btn_load': { ko: '조회', en: 'Load', ja: '取得' },
  'settings.admin_external.btn_loading': { ko: '조회 중…', en: 'Loading…', ja: '取得中...' },
  'settings.admin_external.btn_save_override': {
    ko: '저장 (admin override)',
    en: 'Save (admin override)',
    ja: '保存 (admin override)',
  },
  'settings.admin_external.err_target_invalid': {
    ko: '숫자 user ID 를 입력하세요.',
    en: 'Please enter a numeric user ID.',
    ja: '数値の user ID を入力してください。',
  },
  'settings.admin_external.err_load': {
    ko: '조회 실패: {msg}',
    en: 'Load failed: {msg}',
    ja: '取得失敗: {msg}',
  },
  'settings.admin_external.msg_saved': {
    ko: 'user {id} 매핑 저장 완료',
    en: 'user {id} mapping saved',
    ja: 'user {id} のマッピングを保存しました',
  },

  // ----- AdminFaqSection -----
  'settings.admin_faq.heading': { ko: 'ADMIN — FAQ 관리', en: 'ADMIN — FAQ management', ja: 'ADMIN — FAQ 管理' },
  'settings.admin_faq.desc': {
    ko: 'ADMIN 권한 — FAQ 추가/비활성화. 비활성화는 soft delete (isActive=false), 데이터 보존.',
    en: 'ADMIN privileges — add/disable FAQs. Disable is a soft delete (isActive=false); data is retained.',
    ja: 'ADMIN 権限 — FAQ の追加/無効化。無効化は soft delete (isActive=false) でデータは保持。',
  },
  'settings.admin_faq.label_category': { ko: '카테고리', en: 'Category', ja: 'カテゴリ' },
  'settings.admin_faq.placeholder_category': {
    ko: '계정 / 결제 / 일반 등',
    en: 'e.g. Account / Billing / General',
    ja: '例: アカウント / 課金 / 一般',
  },
  'settings.admin_faq.label_question': { ko: '질문', en: 'Question', ja: '質問' },
  'settings.admin_faq.label_answer': { ko: '답변', en: 'Answer', ja: '回答' },
  'settings.admin_faq.label_sortorder': { ko: '정렬 순서', en: 'Sort order', ja: '並び順' },
  'settings.admin_faq.btn_add': { ko: 'FAQ 추가', en: 'Add FAQ', ja: 'FAQ を追加' },
  'settings.admin_faq.btn_adding': { ko: '추가 중…', en: 'Adding…', ja: '追加中...' },
  'settings.admin_faq.list_subtitle': {
    ko: '현재 FAQ 목록 ({count})',
    en: 'Current FAQ list ({count})',
    ja: '現在の FAQ 一覧 ({count})',
  },
  'settings.admin_faq.list_loading_count': { ko: '…', en: '…', ja: '…' },
  'settings.admin_faq.btn_disable': { ko: '비활성화', en: 'Disable', ja: '無効化' },
  'settings.admin_faq.aria_delete': {
    ko: '{question} 삭제',
    en: 'Delete {question}',
    ja: '{question} を削除',
  },
  'settings.admin_faq.confirm_disable': {
    ko: '이 FAQ 를 비활성화 하시겠습니까?',
    en: 'Disable this FAQ?',
    ja: 'この FAQ を無効化しますか?',
  },
  'settings.admin_faq.err_required': {
    ko: '카테고리, 질문, 답변 모두 필수입니다.',
    en: 'Category, question, and answer are all required.',
    ja: 'カテゴリ、質問、回答はすべて必須です。',
  },
  'settings.admin_faq.err_list': {
    ko: '목록 조회 실패: {msg}',
    en: 'Failed to load list: {msg}',
    ja: '一覧取得失敗: {msg}',
  },
  'settings.admin_faq.err_add': {
    ko: '추가 실패: {msg}',
    en: 'Add failed: {msg}',
    ja: '追加失敗: {msg}',
  },
  'settings.admin_faq.err_delete': {
    ko: '삭제 실패: {msg}',
    en: 'Delete failed: {msg}',
    ja: '削除失敗: {msg}',
  },
  'settings.admin_faq.msg_added': { ko: 'FAQ 추가됨', en: 'FAQ added', ja: 'FAQ を追加しました' },
  'settings.admin_faq.msg_disabled': { ko: 'FAQ 비활성화됨', en: 'FAQ disabled', ja: 'FAQ を無効化しました' },

  // ----- AdminBulkImportUserSection (CSV 일괄 등록) -----
  'settings.admin_import.heading': { ko: 'User 일괄 등록 (CSV)', en: 'Bulk import users (CSV)', ja: 'ユーザー一括登録 (CSV)' },
  'settings.admin_import.hint_columns_lead': { ko: 'CSV 컬럼:', en: 'CSV columns:', ja: 'CSV カラム:' },
  'settings.admin_import.hint_columns_required': { ko: '(필수)', en: '(required)', ja: '(必須)' },
  'settings.admin_import.hint_columns_optional': { ko: '(선택)', en: '(optional)', ja: '(任意)' },
  'settings.admin_import.hint_columns_default': {
    ko: ', default STAFF',
    en: ', default STAFF',
    ja: ', デフォルト STAFF',
  },
  'settings.admin_import.hint_password_optional': {
    ko: '(선택, 8자 이상)',
    en: '(optional, 8+ chars)',
    ja: '(任意、8文字以上)',
  },
  'settings.admin_import.hint_limit': {
    ko: '한 번에 최대 10,000 행 / 5 MB.',
    en: 'Up to 10,000 rows / 5 MB per upload.',
    ja: '1回あたり最大 10,000 行 / 5 MB。',
  },
  'settings.admin_import.hint_institution': {
    ko: '는 자동 (현재 ADMIN 의 기관).',
    en: 'is automatic (current ADMIN\'s institution).',
    ja: 'は自動 (現在の ADMIN の機関)。',
  },
  'settings.admin_import.hint_password_note': {
    ko: 'password 미설정 시 사용자는 별도 초기 비밀번호 발급 또는 invite 절차 필요 (login 차단 상태로 생성됨).',
    en: 'If password is unset, users will need a separate initial password issuance or invite (created with login blocked).',
    ja: 'password 未設定の場合、別途の初期パスワード発行または invite が必要 (login ブロック状態で作成)。',
  },
  'settings.admin_import.aria_file': { ko: 'CSV file', en: 'CSV file', ja: 'CSV ファイル' },
  'settings.admin_import.btn_upload': { ko: 'CSV 업로드', en: 'Upload CSV', ja: 'CSV アップロード' },
  'settings.admin_import.btn_uploading': { ko: '업로드 중…', en: 'Uploading…', ja: 'アップロード中...' },
  'settings.admin_import.err_file_size': {
    ko: '파일 크기 {mb} MB — 최대 5 MB',
    en: 'File size {mb} MB — max 5 MB',
    ja: 'ファイルサイズ {mb} MB — 最大 5 MB',
  },
  'settings.admin_import.err_no_file': {
    ko: 'CSV 파일을 선택해주세요.',
    en: 'Please select a CSV file.',
    ja: 'CSV ファイルを選択してください。',
  },
  'settings.admin_import.summary_lead': {
    ko: '총 {total}행',
    en: 'Total {total} rows',
    ja: '合計 {total} 行',
  },
  'settings.admin_import.summary_success_fail': {
    ko: '— 성공 {success} / 실패 {failure}',
    en: '— success {success} / failure {failure}',
    ja: '— 成功 {success} / 失敗 {failure}',
  },
  'settings.admin_import.aborted': { ko: ' (중단됨)', en: ' (aborted)', ja: ' (中断)' },
  'settings.admin_import.audit_failed': {
    ko: '⚠️ audit 기록 실패 — admin 이 별도 추적 필요 (server 로그 확인)',
    en: '⚠️ audit recording failed — admin must track separately (check server logs)',
    ja: '⚠️ audit 記録失敗 — admin が別途追跡必要 (server ログ参照)',
  },
  'settings.admin_import.summary_aria': { ko: '오류 코드 빈도 상위', en: 'Top error code frequency', ja: 'エラーコード頻度 上位' },
  'settings.admin_import.summary_title': {
    ko: '오류 요약 (상위 {n}):',
    en: 'Error summary (top {n}):',
    ja: 'エラー要約 (上位 {n}):',
  },
  'settings.admin_import.summary_count_suffix': { ko: '건', en: '', ja: '件' },
  'settings.admin_import.table.col_row': { ko: '행', en: 'Row', ja: '行' },
  'settings.admin_import.table.col_email': { ko: 'email', en: 'email', ja: 'email' },
  'settings.admin_import.table.col_code': { ko: 'code', en: 'code', ja: 'code' },
  'settings.admin_import.table.col_message': { ko: '메시지', en: 'Message', ja: 'メッセージ' },
  'settings.admin_import.overflow_all_warn': {
    ko: '전체 {n}건 표시 중 — DOM 무게 주의 (브라우저가 느려질 수 있음).',
    en: 'Showing all {n} — DOM weight warning (browser may slow).',
    ja: '全 {n} 件表示中 — DOM 重量に注意 (ブラウザが遅くなる可能性)。',
  },
  'settings.admin_import.overflow_show_top': {
    ko: '상위 {n}건만 보기',
    en: 'Show only top {n}',
    ja: '上位 {n} 件のみ表示',
  },
  'settings.admin_import.overflow_top_with_hidden': {
    ko: '상위 {visible}건 표시 — 외 {hidden}건 생략 (빈도 요약과 server audit 참조).',
    en: 'Showing top {visible} — {hidden} more hidden (see frequency summary and server audit).',
    ja: '上位 {visible} 件表示 — 残り {hidden} 件省略 (頻度要約と server audit を参照)。',
  },
  'settings.admin_import.overflow_show_all': {
    ko: '전체 {n}건 보기',
    en: 'Show all {n}',
    ja: '全 {n} 件を表示',
  },
  // ----- AdminBulkImportUserSection — 14 code hints + action -----
  // 도메인 한국어 텍스트가 backend code 와 의미 결합 ↑. ko/en/ja 모두 동등 표현 권장.
  'settings.admin_import.code.EMAIL_MISSING': {
    ko: '해당 행에 email 컬럼 채워서 재업로드',
    en: 'Fill the email column for this row and re-upload',
    ja: '該当行の email カラムを埋めて再アップロード',
  },
  'settings.admin_import.code.NAME_MISSING': {
    ko: '해당 행에 name 컬럼 채워서 재업로드',
    en: 'Fill the name column for this row and re-upload',
    ja: '該当行の name カラムを埋めて再アップロード',
  },
  'settings.admin_import.code.EMAIL_INVALID': {
    ko: 'consecutive dot / multi-@ / 시작·끝 dot 제거 후 재업로드',
    en: 'Remove consecutive dots / multi-@ / leading/trailing dots and re-upload',
    ja: '連続ドット / 複数 @ / 先頭末尾ドットを除去して再アップロード',
  },
  'settings.admin_import.code.EMAIL_TOO_LONG': {
    ko: '짧은 email 으로 변경 (최대 255자)',
    en: 'Use a shorter email (max 255 chars)',
    ja: '短い email に変更 (最大 255 文字)',
  },
  'settings.admin_import.code.NAME_TOO_LONG': {
    ko: '짧은 name 으로 변경 (최대 50자)',
    en: 'Use a shorter name (max 50 chars)',
    ja: '短い name に変更 (最大 50 文字)',
  },
  'settings.admin_import.code.EMPLOYEE_NUMBER_TOO_LONG': {
    ko: '짧은 사원번호로 변경 (최대 32자)',
    en: 'Use a shorter employee number (max 32 chars)',
    ja: '短い社員番号に変更 (最大 32 文字)',
  },
  'settings.admin_import.code.ROLE_INVALID': {
    ko: 'role 컬럼을 ADMIN/MANAGER/STAFF 중 하나로 수정',
    en: 'Set role column to one of ADMIN/MANAGER/STAFF',
    ja: 'role カラムを ADMIN/MANAGER/STAFF のいずれかに修正',
  },
  'settings.admin_import.code.PASSWORD_TOO_SHORT': {
    ko: '8자 이상으로 변경 또는 비워두기 (login 차단으로 생성)',
    en: 'Use 8+ chars or leave blank (created with login blocked)',
    ja: '8 文字以上に変更または空欄 (login ブロック状態で作成)',
  },
  'settings.admin_import.code.PASSWORD_HASH_FAILED': {
    ko: 'bcrypt 처리 실패 — password 형식 재확인 또는 server 로그 참조',
    en: 'bcrypt failed — re-check password format or see server logs',
    ja: 'bcrypt 処理失敗 — password 形式を再確認、または server ログ参照',
  },
  'settings.admin_import.code.DUPLICATE_IN_CSV': {
    ko: 'CSV 안 중복 행 제거 후 재업로드',
    en: 'Remove duplicate rows in the CSV and re-upload',
    ja: 'CSV 内の重複行を削除して再アップロード',
  },
  'settings.admin_import.code.EMAIL_EXISTS': {
    ko: '추가 작업 불필요 — 기존 사용자 유지 (skip)',
    en: 'No action needed — existing user retained (skip)',
    ja: '追加作業不要 — 既存ユーザーを維持 (skip)',
  },
  'settings.admin_import.code.DELETED_EMAIL_EXISTS': {
    ko: '이전 사용자 (탈퇴) 가 동일 email 보유. hard-delete 후 재등록 필요',
    en: 'A previously deleted user has the same email. Hard-delete then re-register.',
    ja: '以前のユーザー (退会済) が同一 email を保持。hard-delete 後に再登録が必要',
  },
  'settings.admin_import.code.INSTITUTION_NOT_FOUND': {
    ko: 'institution 비존재·비활성 — admin 의 institution 확인',
    en: 'Institution missing or inactive — check the admin\'s institution',
    ja: 'institution 不在・非活性 — admin の institution を確認',
  },
  'settings.admin_import.code.FK_VIOLATION': {
    ko: 'foreign key 제약 위반 — server 로그 참조',
    en: 'Foreign key violation — see server logs',
    ja: '外部キー制約違反 — server ログ参照',
  },
  'settings.admin_import.code.CREATE_FAILED': {
    ko: 'create 실패 — server 로그 참조',
    en: 'Create failed — see server logs',
    ja: 'create 失敗 — server ログ参照',
  },
  'settings.admin_import.code.UNKNOWN': {
    ko: '서버 응답 코드 — 클라이언트 미정의 (서버 업데이트 후 frontend 갱신 필요)',
    en: 'Server response code — undefined in client (frontend update required after server update)',
    ja: 'サーバー応答コード — クライアント未定義 (サーバー更新後に frontend 更新が必要)',
  },
  'settings.admin_import.action.manual': {
    ko: '관리자 매뉴얼 참조',
    en: 'See admin manual',
    ja: '管理者マニュアル参照',
  },
  'settings.admin_import.action.server_log': {
    ko: 'server 로그 확인',
    en: 'Check server logs',
    ja: 'server ログ確認',
  },

  // ===== 2026-08-12 — 설정 모달(SettingsModal, all-frame #2e) =====
  // 프로필 팝오버 → 설정. 앱 내부 모달이라 설치형(PWA/Electron)에서도 그대로 동작한다.
  'settings.modal.title': { ko: '설정', en: 'Settings', ja: '設定' },
  'settings.modal.nav_aria': { ko: '설정 카테고리', en: 'Settings categories', ja: '設定カテゴリ' },
  'settings.modal.cat.account': { ko: '내 정보 관리', en: 'My account', ja: 'マイ情報管理' },
  // 받은 알림 목록 — «알림설정»(어떤 알림을 받을지)과 다른 자리다.
  'settings.modal.cat.inbox': { ko: '내 알림', en: 'My notifications', ja: 'マイ通知' },
  'settings.modal.cat.preferences': { ko: '환경설정', en: 'Preferences', ja: '環境設定' },
  'settings.modal.cat.security': { ko: '보안설정', en: 'Security', ja: 'セキュリティ設定' },
  'settings.modal.cat.notifications': { ko: '알림설정', en: 'Notifications', ja: '通知設定' },
  'settings.modal.cat.download': { ko: '다운로드', en: 'Downloads', ja: 'ダウンロード' },
  // 아직 동작하지 않는 항목의 공통 표기 — 눌리는 척하는 스위치를 만들지 않기 위한 문구.
  'settings.modal.pending_badge': { ko: '준비 중', en: 'Coming soon', ja: '準備中' },
  'settings.modal.pending_note': {
    ko: '화면만 구성된 항목입니다. 실제 동작은 다음 단계에서 연결됩니다.',
    en: 'This screen is a placeholder. The actual behavior ships in a later step.',
    ja: '画面のみの項目です。実際の動作は次の段階で接続されます。',
  },

  // ===== 2026-08-20 — 외부 메일 계정(migration 107) =====
  // 개인설정 > 메일 (2026-08-20) — 메일 화면 기본값이 코드에 박혀 있던 것을 사용자에게 돌려준다.
  'settings.modal.cat.mail': { ko: '메일', en: 'Mail', ja: 'メール' },
  'settings.mailpref.basic': { ko: '기본 설정', en: 'Basics', ja: '基本設定' },
  'settings.mailpref.write': { ko: '쓰기', en: 'Composing', ja: '作成' },
  'settings.mailpref.read': { ko: '읽기', en: 'Reading', ja: '閲覧' },
  'settings.mailpref.basic_desc': {
    ko: '메일을 열었을 때 어디부터 보일지 정합니다.',
    en: 'Choose what you see when mail opens.',
    ja: 'メールを開いたときに最初に表示する画面を選びます。',
  },
  'settings.mailpref.write_desc': {
    ko: '새 메일을 쓸 때의 기본값입니다. 쓰는 도중에는 언제든 바꿀 수 있습니다.',
    en: 'Defaults for new mail. You can still change them while writing.',
    ja: '新規メールの既定値です。作成中にいつでも変更できます。',
  },
  'settings.mailpref.read_desc': {
    ko: '목록과 읽기 화면이 무엇을 보여줄지 정합니다.',
    en: 'Choose what the list and reading pane show.',
    ja: '一覧と閲覧画面の表示内容を選びます。',
  },
  'settings.mailpref.first_folder': { ko: '메일 첫 화면', en: 'Opening folder', ja: '最初のフォルダ' },
  'settings.mailpref.page_size': { ko: '목록 표시 수', en: 'Messages per page', ja: '一覧の表示件数' },
  'settings.mailpref.page_size_hint': {
    ko: '서버가 한 번에 주는 최대치가 100입니다.',
    en: 'The server returns at most 100 at a time.',
    ja: 'サーバーが一度に返す上限は100件です。',
  },
  'settings.mailpref.compose_mode': { ko: '쓰기 방식', en: 'Compose mode', ja: '作成方式' },
  'settings.mailpref.mode_html': { ko: '서식 있음 (HTML)', en: 'Rich text (HTML)', ja: '書式あり (HTML)' },
  'settings.mailpref.mode_text': { ko: '텍스트', en: 'Plain text', ja: 'テキスト' },
  'settings.mailpref.mode_md': { ko: '마크다운', en: 'Markdown', ja: 'Markdown' },
  'settings.mailpref.quote': { ko: '원본 구분자', en: 'Quote original', ja: '原文の引用' },
  'settings.mailpref.quote_hint': {
    ko: '답장·전달할 때 원본 앞에 인용 표시를 붙입니다.',
    en: 'Adds a quote marker before the original when replying or forwarding.',
    ja: '返信・転送時に原文の前へ引用記号を付けます。',
  },
  'settings.mailpref.simplify': { ko: '제목 간단히', en: 'Simplify subject', ja: '件名を簡潔に' },
  'settings.mailpref.simplify_hint': {
    ko: '목록에서 RE: · FW: 가 여러 번 붙은 것을 한 번으로 줄입니다.',
    en: 'Collapses repeated RE: / FW: prefixes in the list.',
    ja: '一覧で繰り返された RE:・FW: を1つにまとめます。',
  },
  'settings.mailpref.on': { ko: '사용 함', en: 'On', ja: '使用する' },
  'settings.mailpref.off': { ko: '사용 안 함', en: 'Off', ja: '使用しない' },
  'settings.mailpref.saved': { ko: '저장했습니다', en: 'Saved', ja: '保存しました' },
  'settings.mailpref.legacy': {
    ko: '이 서버는 아직 메일 설정을 저장하지 않습니다.',
    en: 'This server does not store mail settings yet.',
    ja: 'このサーバーはまだメール設定を保存しません。',
  },
  'settings.mailacc.heading': { ko: '외부 메일 계정', en: 'External mail accounts', ja: '外部メールアカウント' },
  'settings.mailacc.desc': {
    ko: '회사 메일함 밖의 개인·업무 메일을 Axi 안에서 함께 봅니다. 여러 개 등록할 수 있습니다.',
    en: 'Bring personal or other work mailboxes into Axi. You can add more than one.',
    ja: '社外の個人・業務メールをAxi内でまとめて見られます。複数登録できます。',
  },
  'settings.mailacc.protocol_note': {
    ko: 'SMTP는 보내는 규약입니다 — 그것만 등록하면 보내기만 되고 받은 편지함은 비어 있습니다. 받으려면 IMAP(권장) 또는 POP3를 함께 넣어야 합니다.',
    en: 'SMTP is for sending. Register only SMTP and you can send but your inbox stays empty — add IMAP (recommended) or POP3 to receive.',
    ja: 'SMTPは送信用です。SMTPだけでは送信のみで受信箱は空のままです。受信にはIMAP（推奨）またはPOP3が必要です。',
  },
  'settings.mailacc.loading': { ko: '불러오는 중…', en: 'Loading…', ja: '読み込み中…' },
  'settings.mailacc.empty': {
    ko: '등록된 계정이 없습니다.',
    en: 'No accounts yet.',
    ja: '登録されたアカウントがありません。',
  },
  'settings.mailacc.add': { ko: '계정 추가', en: 'Add account', ja: 'アカウント追加' },
  'settings.mailacc.add_desc': {
    ko: '받기(IMAP)와 보내기(SMTP)를 함께 확인합니다. 저장하기 전에 실제로 접속해 봅니다.',
    en: 'Checks receiving (IMAP) and sending (SMTP) together — it really connects before saving.',
    ja: '受信(IMAP)と送信(SMTP)を一緒に確認します。保存前に実際に接続します。',
  },
  'settings.mailacc.save_and_test': { ko: '연결 확인하고 저장', en: 'Test and save', ja: '接続確認して保存' },
  'settings.mailacc.testing': { ko: '연결 확인 중…', en: 'Testing…', ja: '接続確認中…' },
  'settings.mailacc.retest': { ko: '다시 확인', en: 'Re-test', ja: '再確認' },
  'settings.mailacc.remove': { ko: '계정 삭제', en: 'Remove account', ja: 'アカウント削除' },
  // 가져오기 (migration 112, 2026-08-20) — '다시 확인'(연결)과 '지금 가져오기'(메일)는 다른 일이다.
  'settings.mailacc.sync_now': { ko: '지금 가져오기', en: 'Fetch now', ja: '今すぐ取得' },
  'settings.mailacc.syncing': { ko: '가져오는 중…', en: 'Fetching…', ja: '取得中…' },
  'settings.mailacc.sync_done': {
    ko: '{n}통 가져왔습니다',
    en: 'Fetched {n} message(s)',
    ja: '{n}件取得しました',
  },
  'settings.mailacc.sync_none': {
    ko: '새로 온 메일이 없습니다',
    en: 'No new mail',
    ja: '新着メールはありません',
  },
  'settings.mailacc.sync_more': {
    ko: '{n}통 가져왔습니다 — 남은 메일은 잠시 뒤 이어서 가져옵니다',
    en: 'Fetched {n} — the rest will follow shortly',
    ja: '{n}件取得しました — 残りはまもなく続けて取得します',
  },
  'settings.mailacc.sync_last': {
    ko: '마지막 가져오기 {when} · {n}통',
    en: 'Last fetch {when} · {n} message(s)',
    ja: '最終取得 {when} · {n}件',
  },
  'settings.mailacc.sync_never': {
    ko: '아직 가져온 적 없음',
    en: 'Never fetched',
    ja: 'まだ取得していません',
  },
  'settings.mailacc.sync_unsupported': {
    ko: '이 서버는 아직 가져오기를 지원하지 않습니다 (서버 업데이트 필요)',
    en: 'This server does not support fetching yet (server update required)',
    ja: 'このサーバーはまだ取得に対応していません（サーバー更新が必要）',
  },
  'settings.mailacc.g_recv': { ko: '받기 (IMAP)', en: 'Receiving (IMAP)', ja: '受信 (IMAP)' },
  'settings.mailacc.g_send': { ko: '보내기 (SMTP)', en: 'Sending (SMTP)', ja: '送信 (SMTP)' },
  'settings.mailacc.f_email': { ko: '메일 주소', en: 'Email address', ja: 'メールアドレス' },
  'settings.mailacc.f_recv_host': { ko: '받는 서버', en: 'Incoming server', ja: '受信サーバー' },
  'settings.mailacc.f_protocol': { ko: '받기 방식', en: 'Receiving protocol', ja: '受信方式' },
  'settings.mailacc.proto_imap': { ko: 'IMAP (권장)', en: 'IMAP (recommended)', ja: 'IMAP（推奨）' },
  'settings.mailacc.proto_pop3': { ko: 'POP3', en: 'POP3', ja: 'POP3' },
  'settings.mailacc.pop3_note': {
    ko: 'POP3는 읽음 표시·폴더가 기기끼리 공유되지 않습니다. Axi는 서버의 메일을 지우지 않지만, 휴대폰과 함께 쓸 예정이면 IMAP을 권합니다.',
    en: 'POP3 does not share read state or folders across devices. Axi never deletes from the server, but IMAP is better if you also use a phone.',
    ja: 'POP3は既読・フォルダーが端末間で共有されません。Axiはサーバー上のメールを削除しませんが、スマホと併用するならIMAPを推奨します。',
  },
  'settings.mailacc.f_smtp_host': { ko: '보내는 서버', en: 'Outgoing server', ja: '送信サーバー' },
  'settings.mailacc.f_port': { ko: '포트', en: 'Port', ja: 'ポート' },
  'settings.mailacc.f_user': { ko: '계정', en: 'Username', ja: 'アカウント' },
  'settings.mailacc.f_pass': { ko: '비밀번호', en: 'Password', ja: 'パスワード' },
  'settings.mailacc.same_as_recv': {
    ko: '받기와 같은 계정·비밀번호 사용',
    en: 'Use the same account and password as receiving',
    ja: '受信と同じアカウント・パスワードを使う',
  },
  'settings.mailacc.not_set': { ko: '설정하지 않음', en: 'Not configured', ja: '未設定' },
  'settings.mailacc.recv_ok': { ko: '받기 연결됨', en: 'Receiving connected', ja: '受信接続済み' },
  'settings.mailacc.recv_fail': { ko: '받기 실패', en: 'Receiving failed', ja: '受信失敗' },
  'settings.mailacc.recv_none': { ko: '받기 미설정', en: 'Receiving not set', ja: '受信未設定' },
  'settings.mailacc.send_ok': { ko: '보내기 연결됨', en: 'Sending connected', ja: '送信接続済み' },
  'settings.mailacc.send_fail': { ko: '보내기 실패', en: 'Sending failed', ja: '送信失敗' },
  'settings.mailacc.send_none': { ko: '보내기 미설정', en: 'Sending not set', ja: '送信未設定' },
  'settings.mailacc.every': { ko: '{min}분마다 확인', en: 'checks every {min} min', ja: '{min}分ごとに確認' },
  'settings.mailacc.state_ok': { ko: '정상', en: 'OK', ja: '正常' },
  'settings.mailacc.state_send_only': { ko: '보내기만', en: 'Send only', ja: '送信のみ' },
  'settings.mailacc.state_bad': { ko: '확인 필요', en: 'Needs attention', ja: '要確認' },
  // 보안 연결 — 이 칸이 없어서 465(SSL) 를 넣어도 평문으로 붙어 '보내기 실패' 가 났다(2026-08-20).
  'settings.mailacc.f_secure': { ko: '보안 연결 (SSL/TLS)', en: 'Secure connection (SSL/TLS)', ja: 'セキュア接続 (SSL/TLS)' },
  'settings.mailacc.secure_note': {
    ko: '포트에 맞춰 자동으로 정해진다 — 465·993·995 는 SSL/TLS, 587·143·110 은 끈 채로 STARTTLS 를 쓴다. 직접 바꿀 수도 있다.',
    en: 'Set automatically from the port — 465/993/995 use SSL/TLS, 587/143/110 stay off and use STARTTLS. You can override it.',
    ja: 'ポートに合わせて自動設定 — 465・993・995 は SSL/TLS、587・143・110 はオフのまま STARTTLS。手動変更も可能。',
  },
  'settings.mailacc.edit': { ko: '계정 수정', en: 'Edit account', ja: 'アカウント編集' },
  'settings.mailacc.edit_desc': {
    ko: '서버·포트·보안 연결을 고칠 수 있다. 비밀번호는 비워 두면 기존 값을 그대로 쓴다.',
    en: 'Change server, port, or secure connection. Leave the password blank to keep the stored one.',
    ja: 'サーバー・ポート・セキュア接続を変更できます。パスワードを空欄にすると既存の値を維持します。',
  },
  'settings.mailacc.save_changes': { ko: '연결 확인하고 수정', en: 'Test and update', ja: '接続確認して更新' },
  'settings.mailacc.pass_keep': { ko: '비워 두면 기존 비밀번호 유지', en: 'Leave blank to keep current password', ja: '空欄で既存パスワードを維持' },
  'settings.mailacc.sub': { ko: '외부 메일 계정', en: 'External mail', ja: '外部メール' },
  // 2026-09-06 — 메일 갈래를 통째로 「메일 > 환경설정」으로 옮겼다. 옛 자리는 안내만 한다.
  'settings.mailmoved.heading': { ko: '메일 설정', en: 'Mail settings', ja: 'メール設定' },
  'settings.mailmoved.desc': {
    ko: '메일 설정은 메일 화면의 「환경설정」으로 옮겼습니다. 정해 둔 값은 그대로 있습니다.',
    en: 'Mail settings moved to Mail → Settings. Everything you configured is unchanged.',
    ja: 'メール設定はメール画面の「環境設定」へ移りました。設定した値はそのままです。',
  },
  'settings.mailmoved.link': {
    ko: '메일 > 환경설정 열기',
    en: 'Open Mail → Settings',
    ja: 'メール > 環境設定を開く',
  },
  // 2026-09-05 — 화면을 「메일 > 환경설정 > 외부메일」로 옮겼다. 옛 자리는 어디로 갔는지만 알린다.
  'settings.mailacc.moved': {
    ko: '이 설정은 메일 화면의 「환경설정 > 외부메일」로 옮겼습니다. 등록해 둔 계정은 그대로 있습니다.',
    en: 'This moved to Mail → Settings → External mail. Your saved accounts are unchanged.',
    ja: 'この設定はメール画面の「環境設定 > 外部メール」へ移りました。登録済みのアカウントはそのままです。',
  },
  'settings.mailacc.moved_link': {
    ko: '메일 > 환경설정 > 외부메일 열기',
    en: 'Open Mail → Settings → External mail',
    ja: 'メール > 環境設定 > 外部メールを開く',
  },

  // ===== 2026-08-20 — 테마 만들기(migration 106) =====
  'settings.theme.heading': { ko: '테마 만들기', en: 'Customize theme', ja: 'テーマを作る' },
  'settings.theme.desc': {
    ko: '고른 테마 위에 내 색을 얹습니다. 고르지 않은 색은 테마 기본값을 그대로 씁니다. 바꾸면 화면 전체에 바로 보이고, 저장해야 남습니다.',
    en: 'Layer your own colors on the chosen theme. Anything you leave alone keeps the theme default. Changes preview live; save to keep them.',
    ja: '選んだテーマの上に自分の色を重ねます。選ばない色はテーマの既定値のままです。変更は即座に反映され、保存すると残ります。',
  },
  'settings.theme.primary': { ko: '주색', en: 'Primary', ja: 'メインカラー' },
  'settings.theme.primary_desc': {
    ko: '버튼·링크·선택 상태·스크롤 막대가 이 색을 씁니다',
    en: 'Buttons, links, selection, and the scrollbar use this',
    ja: 'ボタン・リンク・選択状態・スクロールバーに使われます',
  },
  'settings.theme.accent': { ko: '보조색', en: 'Accent', ja: 'アクセント' },
  'settings.theme.accent_desc': { ko: '강조 표시에 쓰입니다', en: 'Used for highlights', ja: '強調表示に使われます' },
  'settings.theme.danger': { ko: '위험색', en: 'Danger', ja: '危険色' },
  'settings.theme.danger_desc': { ko: '삭제·오류에 쓰입니다', en: 'Used for delete and errors', ja: '削除・エラーに使われます' },
  'settings.theme.line': { ko: '라인색', en: 'Lines', ja: '罫線' },
  'settings.theme.line_desc': { ko: '테두리·구분선', en: 'Borders and dividers', ja: '枠線・区切り線' },
  'settings.theme.surface': { ko: '표면색', en: 'Surface', ja: '面の色' },
  'settings.theme.surface_desc': { ko: '카드·패널 배경', en: 'Card and panel backgrounds', ja: 'カード・パネルの背景' },
  'settings.theme.pick_custom': { ko: '직접 고르기', en: 'Pick', ja: '直接選ぶ' },
  'settings.theme.use_default': { ko: '기본값으로', en: 'Use default', ja: '既定値に' },
  'settings.theme.radius': { ko: '버튼 모서리', en: 'Corner style', ja: 'ボタンの角' },
  'settings.theme.radius_desc': {
    ko: '버튼·입력이 이 규칙을 따릅니다. 카드는 읽기 쉽도록 과하지 않게 제한합니다',
    en: 'Buttons and inputs follow this. Cards stay readable and are capped',
    ja: 'ボタン・入力に適用。カードは読みやすさのため上限を設けます',
  },
  'settings.theme.radius_square': { ko: '사각형', en: 'Square', ja: '四角' },
  'settings.theme.radius_round': { ko: '둥글게', en: 'Rounded', ja: '丸め' },
  'settings.theme.radius_pill': { ko: '완전 둥글게', en: 'Pill', ja: '完全な丸' },
  'settings.theme.speed': { ko: '바뀌는 속도', en: 'Transition', ja: '切り替え速度' },
  'settings.theme.speed_desc': {
    ko: '테마가 바뀔 때 색이 넘어가는 시간입니다',
    en: 'How long colors take to cross over when the theme changes',
    ja: 'テーマ変更時に色が移り変わる時間です',
  },
  'settings.theme.speed_instant': { ko: '즉시', en: 'Instant', ja: '即時' },
  'settings.theme.speed_smooth': { ko: '부드럽게', en: 'Smooth', ja: 'なめらか' },
  'settings.theme.speed_slow': { ko: '천천히', en: 'Slow', ja: 'ゆっくり' },
  'settings.theme.reset': { ko: '테마 기본값으로 되돌리기', en: 'Reset to theme defaults', ja: 'テーマ既定値に戻す' },
  // 테마 등록·불러오기 (2026-08-20 사용자 지시) — 기본 제공 테마 8종 + 내가 등록한 테마.
  'settings.theme.load': { ko: '테마 불러오기', en: 'Load a theme', ja: 'テーマを読み込む' },
  'settings.theme.load_desc': {
    ko: '기존 테마를 불러와 출발점으로 삼습니다 — 레일 · 상태바 · 보드까지 함께 채워집니다(모드는 그대로). 색을 고친 뒤 "테마 등록" 을 누르면 새 테마가 됩니다.',
    en: 'Start from an existing theme — the rails, status bar and board are filled in too (the light/dark mode stays). Tweak the colors, then "Register theme" saves it as a new one.',
    ja: '既存のテーマを読み込んで出発点にします — レール・ステータスバー・ボードまで一緒に埋まります（モードはそのまま）。色を変えて「テーマ登録」を押すと新しいテーマになります。',
  },
  'settings.theme.builtin': { ko: '기본 제공', en: 'Built-in', ja: '標準テーマ' },
  'settings.theme.mine': { ko: '내 테마', en: 'My themes', ja: 'マイテーマ' },
  'settings.theme.mine_empty': {
    ko: '아직 등록한 테마가 없습니다. 색을 고른 뒤 아래 "테마 등록" 을 누르세요.',
    en: 'No themes registered yet. Pick your colors, then press "Register theme" below.',
    ja: 'まだ登録したテーマがありません。色を選んで下の「テーマ登録」を押してください。',
  },
  'settings.theme.register': { ko: '테마 등록', en: 'Register theme', ja: 'テーマ登録' },
  'settings.theme.register_confirm': { ko: '등록', en: 'Register', ja: '登録' },
  'settings.theme.cancel': { ko: '취소', en: 'Cancel', ja: 'キャンセル' },
  'settings.theme.name_ph': { ko: '테마 이름', en: 'Theme name', ja: 'テーマ名' },
  'settings.theme.name_default': { ko: '내 테마', en: 'My theme', ja: 'マイテーマ' },
  'settings.theme.delete_one': { ko: '삭제', en: 'Delete', ja: '削除' },
  'settings.theme.full': {
    ko: '테마는 최대 {n}개까지 등록할 수 있습니다. 하나를 지운 뒤 다시 등록하세요.',
    en: 'You can register up to {n} themes. Delete one and try again.',
    ja: 'テーマは最大{n}件まで登録できます。1つ削除してからもう一度お試しください。',
  },
  'settings.theme.local_only': {
    ko: '등록한 테마가 이 기기에만 저장됩니다(서버 반영 전). 서버가 준비되면 자동으로 계정에 올라갑니다.',
    en: 'Registered themes are stored on this device only for now. They upload to your account once the server is ready.',
    ja: '登録したテーマは現在この端末にのみ保存されます。サーバー準備後、自動でアカウントに保存されます。',
  },
  // 미리보기 · 영역별 색 (2026-08-20 — 테마 색 확장)
  'settings.theme.preview': { ko: '미리보기', en: 'Preview', ja: 'プレビュー' },
  'settings.theme.preview_desc': {
    ko: '고른 색이 화면에서 어떻게 보이는지 그대로입니다. 테마를 고르면 좌측 레일 · 메뉴 · 우측 레일 · 상태바 · 보드까지 함께 물듭니다.',
    en: 'Exactly how your colors look on screen. Picking a theme also tints the left rail, menu, right rail, status bar and board.',
    ja: '選んだ色が画面でどう見えるかそのままです。テーマを選ぶと左レール・メニュー・右レール・ステータスバー・ボードまで一緒に色づきます。',
  },
  'settings.theme.lg_rail': { ko: '좌측 레일', en: 'Left rail', ja: '左レール' },
  'settings.theme.lg_menu': { ko: '메뉴', en: 'Menu', ja: 'メニュー' },
  'settings.theme.lg_board': { ko: '본문 보드', en: 'Board', ja: '本文ボード' },
  'settings.theme.lg_table': { ko: '표 · 헤더', en: 'Table & header', ja: '表・ヘッダー' },
  'settings.theme.lg_rrail': { ko: '우측 레일', en: 'Right rail', ja: '右レール' },
  'settings.theme.lg_status': { ko: '상태바', en: 'Status bar', ja: 'ステータスバー' },
  'settings.theme.a_bg': { ko: '배경색', en: 'Background', ja: '背景色' },
  'settings.theme.a_rail': { ko: '좌측 레일 ①', en: 'Left rail ①', ja: '左レール ①' },
  'settings.theme.a_menu': { ko: '메뉴 ②', en: 'Menu ②', ja: 'メニュー ②' },
  'settings.theme.a_rrail': { ko: '우측 레일 ⑤', en: 'Right rail ⑤', ja: '右レール ⑤' },
  'settings.theme.a_status': { ko: '상태바 ⑥', en: 'Status bar ⑥', ja: 'ステータスバー ⑥' },
  'settings.theme.a_table': { ko: '표 배경', en: 'Table', ja: '表の背景' },
  'settings.theme.a_thead': { ko: '표 헤더', en: 'Table header', ja: '表ヘッダー' },
  'settings.theme.a_thead_text': { ko: '헤더 글자', en: 'Header text', ja: 'ヘッダーの文字' },
  'settings.theme.a_icon': { ko: '아이콘', en: 'Icons', ja: 'アイコン' },
  'settings.theme.a_icon_hover': { ko: '아이콘 호버', en: 'Icon hover', ja: 'アイコン(ホバー)' },
  'settings.theme.a_tint': { ko: '선택 배경', en: 'Selected background', ja: '選択の背景' },
  'settings.theme.a_text': { ko: '글자', en: 'Text', ja: '文字' },
  'settings.theme.a_text_muted': { ko: '보조 글자', en: 'Secondary text', ja: '補助文字' },
  'settings.theme.a_bg_desc': { ko: '본문 바탕', en: 'Main background', ja: '本文の背景' },
  'settings.theme.a_rail_desc': { ko: '아이콘 레일 배경', en: 'Icon rail background', ja: 'アイコンレール背景' },
  'settings.theme.a_menu_desc': { ko: '2차 메뉴 배경', en: 'Secondary menu background', ja: '2次メニュー背景' },
  'settings.theme.a_rrail_desc': { ko: '우측 레일 배경', en: 'Right rail background', ja: '右レール背景' },
  'settings.theme.a_status_desc': { ko: '하단 상태바 배경', en: 'Status bar background', ja: 'ステータスバー背景' },
  'settings.theme.a_table_desc': { ko: '표 몸통 · 행', en: 'Table body and rows', ja: '表の本体・行' },
  'settings.theme.a_thead_desc': { ko: '표 제목 줄', en: 'Table header row', ja: '表の見出し行' },
  'settings.theme.a_thead_text_desc': { ko: '표 제목 줄의 글자', en: 'Text in the header row', ja: '見出し行の文字' },
  'settings.theme.a_icon_desc': { ko: '레일 · 메뉴 · 아이콘 버튼', en: 'Rails, menu, icon buttons', ja: 'レール・メニュー・アイコンボタン' },
  'settings.theme.a_icon_hover_desc': { ko: '마우스를 올렸을 때', en: 'On hover', ja: 'ホバー時' },
  'settings.theme.a_tint_desc': { ko: '선택된 메뉴 · 배지', en: 'Selected menu and badges', ja: '選択メニュー・バッジ' },
  'settings.theme.a_text_desc': { ko: '본문 글자', en: 'Body text', ja: '本文の文字' },
  'settings.theme.a_text_muted_desc': { ko: '설명 · 부가 정보', en: 'Descriptions and meta', ja: '説明・補足情報' },
  'settings.theme.c_open': { ko: '{name} — 지금 색 보기', en: '{name} — show current color', ja: '{name} — 現在の色を見る' },
  'settings.theme.c_theme_colors': { ko: '이 테마의 색', en: 'Colors in this theme', ja: 'このテーマの色' },
  'settings.theme.c_custom': { ko: '{name} 커스텀 색', en: '{name} custom color', ja: '{name} カスタム色' },
  'settings.theme.c_default': { ko: '기본값', en: 'Default', ja: '既定値' },
  // 회사 기본 테마 잠금 (2026-08-20)
  'settings.theme.locked_title': { ko: '회사 테마가 지정되어 있습니다', en: 'A company theme is in effect', ja: '会社テーマが指定されています' },
  'settings.theme.locked_desc': {
    ko: '관리자가 전사 테마를 적용해 개인 변경을 잠갔습니다. 바꾸려면 관리자에게 문의하세요. 이전에 고른 색은 지워지지 않으며, 잠금이 풀리면 그대로 돌아옵니다.',
    en: 'An administrator applied a company-wide theme and locked personal changes. Ask your administrator to change it — colors you picked before are kept and return if the lock is lifted.',
    ja: '管理者が全社テーマを適用し、個人変更をロックしました。変更は管理者にお問い合わせください。以前選んだ色は消えず、ロック解除で戻ります。',
  },
  'settings.theme.p_default': { ko: '기본', en: 'Default', ja: '既定' },
  'settings.theme.p_default_company': { ko: '회사 기본 테마를 따릅니다', en: 'Follows the company theme', ja: '会社の既定テーマに従います' },
  'settings.theme.p_default_desc': { ko: '색을 얹지 않습니다', en: 'No colour applied', ja: '色を適用しません' },
  'settings.theme.p_ocean': { ko: '오션 블루', en: 'Ocean blue', ja: 'オーシャンブルー' },
  'settings.theme.p_indigo': { ko: '미드나잇 인디고', en: 'Midnight indigo', ja: 'ミッドナイトインディゴ' },
  'settings.theme.p_forest': { ko: '포레스트 그린', en: 'Forest green', ja: 'フォレストグリーン' },
  'settings.theme.p_sunset': { ko: '선셋 오렌지', en: 'Sunset orange', ja: 'サンセットオレンジ' },
  'settings.theme.p_rose': { ko: '로즈 핑크', en: 'Rose pink', ja: 'ローズピンク' },
  'settings.theme.p_purple': { ko: '로열 퍼플', en: 'Royal purple', ja: 'ロイヤルパープル' },
  'settings.theme.p_teal': { ko: '틸 민트', en: 'Teal mint', ja: 'ティールミント' },
  'settings.theme.p_midnight': { ko: '미드나잇 블루', en: 'Midnight blue', ja: 'ミッドナイトブルー' },
  'settings.theme.p_deep_purple': { ko: '딥 퍼플', en: 'Deep purple', ja: 'ディープパープル' },
  'settings.theme.p_dark_forest': { ko: '다크 포레스트', en: 'Dark forest', ja: 'ダークフォレスト' },
  'settings.theme.p_amber_night': { ko: '앰버 나이트', en: 'Amber night', ja: 'アンバーナイト' },
  'settings.theme.p_carbon': { ko: '카본', en: 'Carbon', ja: 'カーボン' },
  // 테마 설정 화면(2026-08-20) — 옛 '화면·외관' 자리.
  'settings.themeset.heading': { ko: '테마 설정', en: 'Theme', ja: 'テーマ設定' },
  'settings.themeset.subtitle': {
    ko: '라이트·다크와 색을 미리 조합해 둔 테마입니다. 카드를 고르면 모드와 색이 함께 바뀌고 즉시 저장됩니다.',
    en: 'Ready-made combinations of light/dark and color. Pick a card and the mode and colors change together, saved instantly.',
    ja: 'ライト・ダークと色をあらかじめ組み合わせたテーマです。カードを選ぶとモードと色が一緒に変わり、すぐ保存されます。',
  },
  'settings.themeset.mode_desc': {
    ko: '카드의 라이트·다크와 별개로 언제든 바꿀 수 있습니다.',
    en: 'You can switch this at any time, independently of the card you picked.',
    ja: 'カードのライト・ダークとは別に、いつでも切り替えられます。',
  },
  'settings.themeset.custom_hint': {
    ko: '색을 직접 정하려면 아래 "테마 만들기" 로 가세요 — 고른 테마를 출발점으로 내 색·모서리·속도를 얹고 이름 붙여 등록할 수 있습니다.',
    en: 'To set colors yourself, go to "Customize theme" below — start from a theme, add your own colors, corners and speed, then register it under a name.',
    ja: '色を自分で決めるには下の「テーマを作る」へ — 選んだテーマを出発点に、色・角・速度を加えて名前を付けて登録できます。',
  },
  'settings.theme.err_save': { ko: '저장 실패 — {msg}', en: 'Save failed — {msg}', ja: '保存失敗 — {msg}' },
  'settings.theme.samples': { ko: '실제 보기', en: 'Live samples', ja: '実際の見え方' },
  'settings.theme.samples_desc': {
    ko: '지금 고른 색이 화면에서 어떻게 보이는지 그대로입니다.',
    en: 'Exactly how the colors you picked look in the app.',
    ja: '今選んだ色が画面でどう見えるかそのままです。',
  },
  'settings.theme.s_buttons': { ko: '버튼', en: 'Buttons', ja: 'ボタン' },
  'settings.theme.s_states': { ko: '상태 색', en: 'Status colors', ja: '状態の色' },
  'settings.theme.s_filters': { ko: '필터 · 입력', en: 'Filters & input', ja: 'フィルター・入力' },
  'settings.theme.s_scroll': { ko: '스크롤 막대 — 주색과 같이 움직인다', en: 'Scrollbar — follows the primary color', ja: 'スクロールバー — メインカラーに連動' },
  'settings.theme.s_cancel': { ko: '취소', en: 'Cancel', ja: 'キャンセル' },
  'settings.theme.s_delete': { ko: '삭제', en: 'Delete', ja: '削除' },
  'settings.theme.s_more': { ko: '더 보기', en: 'More', ja: 'もっと見る' },
  'settings.theme.s_done': { ko: '완료', en: 'Done', ja: '完了' },
  'settings.theme.s_wait': { ko: '대기', en: 'Waiting', ja: '待機' },
  'settings.theme.s_late': { ko: '지연', en: 'Late', ja: '遅延' },
  'settings.theme.s_doing': { ko: '진행', en: 'In progress', ja: '進行' },
  'settings.theme.s_all': { ko: '전체', en: 'All', ja: '全体' },
  'settings.theme.s_mine': { ko: '내 문서', en: 'Mine', ja: '自分の文書' },
  'settings.theme.s_shared': { ko: '공유됨', en: 'Shared', ja: '共有済み' },
  'settings.theme.s_search': { ko: '검색어를 입력하세요', en: 'Search', ja: '検索語を入力' },
  'settings.theme.s_scroll_1': { ko: '스크롤 막대에도 주색이 그대로 붙습니다.', en: 'The scrollbar takes the primary color too.', ja: 'スクロールバーにもメインカラーが付きます。' },
  'settings.theme.s_scroll_2': { ko: '테마를 바꾸면 막대 색도 함께 바뀝니다.', en: 'Change the theme and the bar changes with it.', ja: 'テーマを変えるとバーの色も変わります。' },
  'settings.theme.s_scroll_3': { ko: '이 영역을 굴려 보세요.', en: 'Scroll this area.', ja: 'この領域をスクロールしてみてください。' },
  'settings.theme.s_scroll_4': { ko: '막대가 주색으로 칠해져 있습니다.', en: 'The bar is painted in the primary color.', ja: 'バーがメインカラーで塗られています。' },

  // 프로필 새 항목 6종 (migration 106)
  'settings.profile.label_name_en': { ko: '영문 이름', en: 'Name (English)', ja: '英文名' },
  'settings.profile.label_extension': { ko: '내선', en: 'Extension', ja: '内線' },
  'settings.profile.label_desk': { ko: '자리', en: 'Desk', ja: '座席' },
  'settings.profile.label_bio': { ko: '한 줄 소개', en: 'About me', ja: '一言紹介' },
  'settings.profile.label_birthday': { ko: '생일', en: 'Birthday', ja: '誕生日' },
  'settings.profile.label_timezone': { ko: '시간대', en: 'Time zone', ja: 'タイムゾーン' },
  'settings.profile.ph_name_en': { ko: 'Hyunwoo Kim', en: 'Hyunwoo Kim', ja: 'Hyunwoo Kim' },
  'settings.profile.ph_extension': { ko: '1234', en: '1234', ja: '1234' },
  'settings.profile.ph_desk': { ko: '본사 7층 A-12', en: 'HQ 7F A-12', ja: '本社7階 A-12' },
  'settings.profile.ph_bio': { ko: '무슨 일을 하는지 한 줄로', en: 'One line about what you do', ja: '担当を一言で' },
  'settings.profile.ph_birthday': { ko: 'MM-DD (예: 03-14)', en: 'MM-DD (e.g. 03-14)', ja: 'MM-DD（例: 03-14）' },
  'settings.profile.err_birthday': {
    ko: '생일은 MM-DD 형식으로 입력하세요 (예: 03-14)',
    en: 'Enter the birthday as MM-DD (e.g. 03-14)',
    ja: '誕生日は MM-DD 形式で入力してください（例: 03-14）',
  },
  'settings.profile.card_intro': { ko: '소개', en: 'About', ja: '紹介' },
  // 2026-09-13 사용자 결정 — 이름·영문 이름·생일·사진은 인사에서 관리자가 고친다(본인 저장은 서버 403).
  'settings.profile.hr_locked': {
    ko: '이름·영문 이름·생일·프로필 사진은 인사 담당자가 관리합니다 — 바꿀 것이 있으면 인사 담당자에게 요청하세요',
    en: 'Name, English name, birthday and photo are managed by HR — ask your HR manager to change them',
    ja: '名前・英文名・誕生日・プロフィール写真は人事担当が管理します — 変更は人事担当にご依頼ください',
  },
  'settings.profile.photo_readonly': {
    ko: '사진은 인사 담당자에게 요청해 바꿉니다',
    en: 'Ask HR to change your photo',
    ja: '写真の変更は人事担当にご依頼ください',
  },

  // ===== 2026-08-20 — 프로필 재설계(히어로 + 조직·계정 카드 + 사진 효과) =====
  'settings.profile.card_org': { ko: '조직', en: 'Organization', ja: '組織' },
  'settings.profile.card_account': { ko: '계정', en: 'Account', ja: 'アカウント' },
  'settings.profile.company': { ko: '회사', en: 'Company', ja: '会社' },
  'settings.profile.division': { ko: '부서', en: 'Division', ja: '部署' },
  'settings.profile.team': { ko: '팀', en: 'Team', ja: 'チーム' },
  'settings.profile.employee_no': { ko: '사번', en: 'Employee no.', ja: '社員番号' },
  'settings.profile.team_joined': { ko: '팀 합류일', en: 'Joined team', ja: 'チーム参加日' },
  'settings.profile.team_leader': { ko: '팀장', en: 'Team lead', ja: 'チーム長' },
  'settings.profile.team_member': { ko: '팀원', en: 'Member', ja: 'メンバー' },
  'settings.profile.org_empty': {
    ko: '아직 소속된 팀이 없습니다. 관리자가 조직도에 배정하면 여기에 표시됩니다.',
    en: 'You are not on a team yet. It appears here once an admin assigns you.',
    ja: 'まだ所属チームがありません。管理者が組織図に割り当てると表示されます。',
  },
  'settings.profile.joined_at': { ko: '가입일', en: 'Joined', ja: '登録日' },
  'settings.profile.last_login': { ko: '마지막 로그인', en: 'Last sign-in', ja: '最終ログイン' },
  'settings.profile.account_state': { ko: '계정 상태', en: 'Account status', ja: 'アカウント状態' },
  'settings.profile.state_on': { ko: '사용 중', en: 'Active', ja: '使用中' },
  'settings.profile.state_off': { ko: '중지됨', en: 'Suspended', ja: '停止中' },
  'settings.profile.mail': { ko: '업무 메일', en: 'Work email', ja: '業務メール' },
  'settings.profile.effect_pick': { ko: '사진 효과', en: 'Photo effect', ja: '写真エフェクト' },
  'settings.profile.effect_note': {
    ko: '효과는 내 화면에서만 보입니다. 조직도·채팅의 내 사진에도 붙이는 것은 다음 단계입니다.',
    en: 'Effects show on your screen only. Applying them in the org chart and chat comes next.',
    ja: 'エフェクトは自分の画面にのみ表示されます。組織図・チャットへの適用は次の段階です。',
  },
  'settings.profile.fx.none': { ko: '없음', en: 'None', ja: 'なし' },
  'settings.profile.fx.status': { ko: '상태 링', en: 'Status dot', ja: 'ステータス' },
  'settings.profile.fx.ring': { ko: '그라데이션 링', en: 'Gradient ring', ja: 'グラデーション' },
  'settings.profile.fx.spin': { ko: '회전 그라데이션', en: 'Spinning ring', ja: '回転リング' },
  'settings.profile.fx.pulse': { ko: '펄스', en: 'Pulse', ja: 'パルス' },
  'settings.profile.fx.glow': { ko: '빛 번짐', en: 'Glow', ja: 'グロー' },
  'settings.profile.fx.frame': { ko: '프레임', en: 'Frame', ja: 'フレーム' },

  // ===== 2026-08-19 — 화면잠금(screen-lock). 보안설정 하위 2단계 메뉴 =====
  'settings.modal.sub.password': { ko: '비밀번호 · 기기', en: 'Password & devices', ja: 'パスワード・端末' },
  'settings.modal.sub.screen_lock': { ko: '잠금화면 설정', en: 'Lock screen', ja: 'ロック画面設定' },
  'settings.modal.sub.lock_timing': { ko: '대기시간 설정', en: 'Lock timing', ja: '待機時間設定' },
  'settings.modal.sub_aria': { ko: '보안설정 하위 메뉴', en: 'Security sub-menu', ja: 'セキュリティ設定サブメニュー' },
  'settings.modal.sub.new': { ko: '신규', en: 'New', ja: '新規' },
  // 2026-08-20 하위 메뉴 확장 — 내비에 들어갈 짧은 이름(긴 제목은 196px 에서 두 줄이 된다).
  'settings.modal.sub.mobile': { ko: '등록된 모바일 기기', en: 'Mobile devices', ja: '登録済み端末' },
  'settings.modal.sub.policy': { ko: '약관 · 정책', en: 'Terms & policies', ja: '規約・ポリシー' },
  'settings.modal.sub.channels': { ko: '알림 채널 · 화면별', en: 'Channels & screens', ja: 'チャネル・画面別' },

  'settings.lock.title': { ko: '잠금화면 설정', en: 'Lock screen', ja: 'ロック画面設定' },
  'settings.lock.desc': {
    ko: '자리를 비웠을 때 화면을 덮는 방식입니다. 세 가지 중 원하는 것을 고르세요.',
    en: 'How the screen is covered while you are away. Pick one of three.',
    ja: '離席時に画面を覆う方式です。3つから選べます。',
  },
  'settings.lock.enable': { ko: '화면잠금 사용', en: 'Use screen lock', ja: '画面ロックを使用' },
  'settings.lock.enable_desc': {
    ko: '정한 시간 동안 조작이 없으면 화면을 덮습니다',
    en: 'Covers the screen after the chosen idle time',
    ja: '設定時間だけ操作がないと画面を覆います',
  },
  'settings.lock.design_heading': { ko: '잠금화면 디자인', en: 'Lock screen design', ja: 'ロック画面デザイン' },
  'settings.lock.design.blur': { ko: 'A · 흐림 가림막', en: 'A · Blur', ja: 'A · ぼかし' },
  'settings.lock.design.blur_desc': {
    ko: '뒤 화면을 흐리게. 해제하면 하던 자리로 바로.',
    en: 'Blurs what is behind. You land back where you were.',
    ja: '背面をぼかす。解除すると元の場所へ。',
  },
  'settings.lock.design.cover': { ko: 'B · 불투명 + 시계', en: 'B · Opaque + clock', ja: 'B · 不透明＋時計' },
  'settings.lock.design.cover_desc': {
    ko: '완전히 가림. 공용 좌석·회의실 PC에 맞다.',
    en: 'Hides everything. Good for shared desks.',
    ja: '完全に隠す。共用席に適する。',
  },
  'settings.lock.design.sheet': { ko: 'C · 미니멀', en: 'C · Minimal', ja: 'C · ミニマル' },
  'settings.lock.design.sheet_desc': {
    ko: '배경색으로만 덮는 가장 가벼운 화면.',
    en: 'Just the background color — the lightest option.',
    ja: '背景色だけで覆う最も軽い画面。',
  },
  'settings.lock.preview': { ko: '미리보기', en: 'Preview', ja: 'プレビュー' },
  'settings.lock.preview_note': {
    ko: '미리보기를 누르면 고른 화면이 전체 화면으로 5초간 떴다가 닫힙니다. 설정은 이 기기에만 적용됩니다.',
    en: 'Preview shows the chosen screen full-size for 5 seconds. Settings apply to this device only.',
    ja: 'プレビューは選んだ画面を5秒間全画面で表示します。設定はこの端末のみに適用されます。',
  },
  'settings.lock.selected': { ko: '선택됨', en: 'Selected', ja: '選択中' },

  'settings.lock.timing_title': { ko: '대기시간 설정', en: 'Lock timing', ja: '待機時間設定' },
  'settings.lock.timing_desc': {
    ko: '얼마나 지나면 잠글지, 잠긴 뒤 언제 로그아웃할지 정합니다.',
    en: 'When to lock, and when to sign out after locking.',
    ja: 'いつロックし、ロック後いつログアウトするかを決めます。',
  },
  'settings.lock.idle': { ko: '잠금까지 대기 시간', en: 'Lock after', ja: 'ロックまでの待機時間' },
  'settings.lock.idle_desc': {
    ko: '이 시간 동안 클릭·입력·스크롤이 없으면 잠깁니다',
    en: 'Locks when there is no click, key, or scroll for this long',
    ja: 'この時間クリック・入力・スクロールがないとロックされます',
  },
  'settings.lock.logout': { ko: '잠긴 뒤 자동 로그아웃', en: 'Sign out after locking', ja: 'ロック後の自動ログアウト' },
  'settings.lock.logout_desc': {
    ko: '잠금 화면을 이 시간 동안 열지 않으면 로그아웃합니다',
    en: 'Signs out if the lock screen is not opened within this time',
    ja: 'この時間ロック画面を解除しないとログアウトします',
  },
  'settings.lock.logout_never': { ko: '사용 안 함', en: 'Never', ja: '使用しない' },
  // 실패 허용 횟수 (2026-08-28) — 전에는 코드에 박혀 있었다.
  'settings.lock.fails': {
    ko: '비밀번호 틀림 허용',
    en: 'Wrong password attempts',
    ja: 'パスワード誤入力の許容回数',
  },
  'settings.lock.fails_desc': {
    ko: '이 횟수를 넘기면 로그아웃되어 로그인 화면으로 갑니다.',
    en: 'Past this many tries you are signed out and sent to the sign-in screen.',
    ja: 'この回数を超えるとログアウトし、ログイン画面に移ります。',
  },
  'settings.lock.fails_n': { ko: '{n}회', en: '{n} tries', ja: '{n}回' },
  'settings.lock.prewarn': { ko: '잠기기 5분 전 알려주기', en: 'Warn 5 minutes before locking', ja: 'ロック5分前に通知' },
  'settings.lock.prewarn_desc': {
    ko: '작성 중인 글이 있을 때 놓치지 않도록',
    en: 'So you do not lose what you are typing',
    ja: '作成中の内容を失わないように',
  },
  'settings.lock.lock_now': { ko: '지금 바로 잠그기', en: 'Lock now', ja: '今すぐロック' },
  'settings.lock.lock_now_desc': {
    ko: '자리를 비우기 전에 직접 잠급니다',
    en: 'Lock it yourself before stepping away',
    ja: '離席前に自分でロックします',
  },
  'settings.lock.lock_now_btn': { ko: '지금 잠그기', en: 'Lock now', ja: '今すぐロック' },
  'settings.lock.device_note': {
    ko: '이 설정은 지금 쓰는 기기에만 적용됩니다. 회사 전체 강제는 관리 콘솔에서 지정합니다(2단계).',
    en: 'These settings apply to this device only. Company-wide enforcement comes from the admin console (phase 2).',
    ja: 'この設定はこの端末のみに適用されます。全社強制は管理コンソールで指定します（第2段階）。',
  },
  'settings.lock.min': { ko: '{n}분', en: '{n} min', ja: '{n}分' },
  'settings.lock.hour': { ko: '{n}시간', en: '{n} h', ja: '{n}時間' },
  'settings.lock.disabled_note': {
    ko: '화면잠금이 꺼져 있으면 기존과 같이 60분 무활동 시 자동 로그아웃됩니다.',
    en: 'With screen lock off, you are signed out automatically after 60 idle minutes, as before.',
    ja: '画面ロックがオフの場合、従来どおり60分無操作で自動ログアウトします。',
  },

  // 내 정보 관리 — 약관 및 서비스 정책
  'settings.modal.policy.heading': { ko: '약관 및 서비스 정책', en: 'Terms & policies', ja: '規約およびサービスポリシー' },
  'settings.modal.policy.desc': {
    ko: '서비스 이용에 적용되는 약관과 정책을 확인할 수 있습니다.',
    en: 'Review the terms and policies that apply to this service.',
    ja: 'サービス利用に適用される規約とポリシーを確認できます。',
  },
  'settings.modal.policy.terms': { ko: '서비스 이용약관', en: 'Terms of service', ja: '利用規約' },
  'settings.modal.policy.privacy': { ko: '개인정보 처리방침', en: 'Privacy policy', ja: 'プライバシーポリシー' },
  'settings.modal.policy.oss': { ko: '오픈소스 라이선스 고지', en: 'Open-source notices', ja: 'オープンソースライセンス告知' },

  // 환경설정 — 언어
  'settings.modal.language.heading': { ko: '언어 설정', en: 'Language', ja: '言語設定' },
  'settings.modal.language.desc': {
    ko: '화면에 표시할 언어를 선택하세요. 선택하면 즉시 반영됩니다.',
    en: 'Choose the display language. It applies instantly.',
    ja: '表示する言語を選択してください。即時反映されます。',
  },

  // 보안설정 — 화면만 구성된 3종
  'settings.modal.security.geo_title': { ko: '해외 로그인 차단', en: 'Block overseas sign-in', ja: '海外ログイン遮断' },
  'settings.modal.security.geo_desc': {
    ko: '허용한 국가 밖에서 시도된 로그인을 차단합니다.',
    en: 'Blocks sign-in attempts from outside allowed countries.',
    ja: '許可した国以外からのログインを遮断します。',
  },
  'settings.modal.security.sessions_title': { ko: '로그인 기기 정보', en: 'Sign-in devices', ja: 'ログイン端末情報' },
  'settings.modal.security.sessions_desc': {
    ko: '내 계정에 로그인된 브라우저·기기와 최근 접속 시각을 확인합니다.',
    en: 'See the browsers and devices signed in to your account.',
    ja: 'アカウントにログイン中のブラウザ・端末を確認します。',
  },
  'settings.modal.security.mobile_title': { ko: '등록된 모바일 기기 관리', en: 'Registered mobile devices', ja: '登録済みモバイル端末管理' },
  'settings.modal.security.mobile_desc': {
    ko: '푸시 알림을 받도록 등록된 모바일 기기를 확인하고 해지합니다.',
    en: 'Review and remove mobile devices registered for push notifications.',
    ja: 'プッシュ通知用に登録されたモバイル端末を確認・解除します。',
  },

  // 알림설정 — 화면만
  'settings.modal.notif.heading': { ko: '알림 설정', en: 'Notification settings', ja: '通知設定' },
  'settings.modal.notif.desc': {
    ko: '알림을 받을 채널과 화면을 선택합니다.',
    en: 'Choose which channels and screens send you notifications.',
    ja: '通知を受け取るチャネルと画面を選択します。',
  },
  'settings.modal.notif.channel': { ko: '알림 채널', en: 'Channels', ja: '通知チャネル' },
  'settings.modal.notif.pc': { ko: 'PC', en: 'Desktop', ja: 'PC' },
  'settings.modal.notif.pc_desc': { ko: '브라우저·데스크톱 알림', en: 'Browser and desktop alerts', ja: 'ブラウザ・デスクトップ通知' },
  'settings.modal.notif.mobile': { ko: '모바일', en: 'Mobile', ja: 'モバイル' },
  'settings.modal.notif.mobile_desc': { ko: '모바일 앱 푸시 알림', en: 'Mobile app push', ja: 'モバイルアプリのプッシュ通知' },
  'settings.modal.notif.mail': { ko: '메일', en: 'Email', ja: 'メール' },
  'settings.modal.notif.mail_desc': { ko: '이메일로 다시 알림', en: 'Follow-up by email', ja: 'メールで再通知' },
  // 통합설정(/settings)에서 이관한 실동작 블록 — 알림 종류 4종(user_settings 저장).
  'settings.modal.notif.kinds': { ko: '알림 종류', en: 'Notification types', ja: '通知の種類' },
  'settings.modal.notif.kinds_desc': {
    ko: '종류별로 알림을 받을지 선택합니다. 변경하면 바로 저장됩니다.',
    en: 'Choose which kinds of notifications you receive. Changes save automatically.',
    ja: '種類ごとに通知を受け取るか選択します。変更は自動保存されます。',
  },
  'settings.modal.notif.by_screen': { ko: '화면별 알림', en: 'Per-screen notifications', ja: '画面別通知' },
  'settings.modal.notif.row_desc': { ko: 'PC · 모바일 · 메일', en: 'Desktop · Mobile · Email', ja: 'PC・モバイル・メール' },
  'settings.modal.notif.by_screen_desc': {
    ko: '화면마다 알림을 켜고 끕니다.',
    en: 'Turn notifications on or off per screen.',
    ja: '画面ごとに通知をオン・オフします。',
  },

  // 다운로드 — 앱 설치 안내
  'settings.modal.download.heading': { ko: '앱 다운로드', en: 'Download the app', ja: 'アプリのダウンロード' },
  'settings.modal.download.desc': {
    ko: '모바일과 PC에서도 같은 계정으로 이용할 수 있습니다.',
    en: 'Use the same account on mobile and desktop.',
    ja: 'モバイルとPCでも同じアカウントで利用できます。',
  },
  'settings.modal.download.ios': { ko: 'iPhone · iPad', en: 'iPhone · iPad', ja: 'iPhone · iPad' },
  'settings.modal.download.ios_desc': { ko: 'App Store 에서 설치', en: 'Install from the App Store', ja: 'App Store からインストール' },
  'settings.modal.download.android': { ko: 'Android', en: 'Android', ja: 'Android' },
  'settings.modal.download.android_desc': { ko: 'Google Play 에서 설치', en: 'Install from Google Play', ja: 'Google Play からインストール' },
  'settings.modal.download.pc': { ko: 'Windows · macOS', en: 'Windows · macOS', ja: 'Windows · macOS' },
  'settings.modal.download.pc_desc': { ko: '데스크톱 설치본', en: 'Desktop installer', ja: 'デスクトップ版インストーラ' },
};

export type SettingsI18nKey = keyof typeof MESSAGES;

/**
 * Cycle 59 — SettingsPage i18n lookup.
 * cycle 46 학습 phase 5 fallback chain (lang → en → ko) 적용.
 * params 치환 ({name}) — calendar / workspace / mail / onboarding / board / task 와 일관.
 */
export function tSettings(key: SettingsI18nKey, params?: Record<string, string | number>): string {
  const m = MESSAGES[key];
  if (!m) return key;
  const raw = m[getSharedLang()] ?? m.en ?? m[FALLBACK_LANG] ?? key;
  if (!params) return raw;
  return raw.replace(/\{(\w+)\}/g, (_, name) => {
    const v = params[name];
    return v === undefined || v === null ? `{${name}}` : String(v);
  });
}

// base-i18n thin wrapper (cycle 41 통합 패턴 일관)
export function getSettingsLang(): Lang { return getSharedLang(); }
export function setSettingsLang(value: Lang): void { setSharedLang(value); }
export function subscribeSettingsLang(fn: () => void): () => void {
  return subscribeSharedLang(fn);
}
